/**
 * Loan Sizer Enums
 *
 * Mirror of the Postgres enums owned by 33-pricing-engine-service
 * (`prisma/schema.prisma`). Values are SCREAMING_SNAKE to match the DB
 * representation 1:1 — pricing-engine reads/writes these directly via
 * Prisma, while portals, API Gateway, and other services import from
 * here to satisfy CLAUDE.md §11a (no magic strings).
 *
 * The `as const` object pattern (rather than `enum`) is intentional: the
 * resulting type is a string-literal union, so callers can write either
 * `LoanSizerType.FIX_FLIP` or the literal `'FIX_FLIP'` — matching the
 * shape Prisma itself generates on the pricing-engine side.
 *
 * If you change a value, also update the Prisma enum in
 * `33-pricing-engine-service/prisma/schema.prisma` and ship a migration.
 */

import { z } from 'zod';

/**
 * Loan product type for sizing.
 */
export const LoanSizerType = {
  FIX_FLIP: 'FIX_FLIP',
  GROUND_UP: 'GROUND_UP',
  BRIDGE: 'BRIDGE',
} as const;
export type LoanSizerType = (typeof LoanSizerType)[keyof typeof LoanSizerType];

export const LoanSizerTypeSchema = z.nativeEnum(LoanSizerType);

/**
 * Severity of a sizing flag surfaced alongside the result.
 */
export const FlagSeverity = {
  INFO: 'INFO',
  WARN: 'WARN',
  EXCEPTION: 'EXCEPTION',
} as const;
export type FlagSeverity = (typeof FlagSeverity)[keyof typeof FlagSeverity];

export const FlagSeveritySchema = z.nativeEnum(FlagSeverity);

/**
 * Outcome classification for a computed sizing vs. the requested loan.
 */
export const SizingStatus = {
  WITHIN_TARGET: 'WITHIN_TARGET',
  EXCEPTION: 'EXCEPTION',
  OUTSIDE: 'OUTSIDE',
} as const;
export type SizingStatus = (typeof SizingStatus)[keyof typeof SizingStatus];

export const SizingStatusSchema = z.nativeEnum(SizingStatus);

/**
 * Surface the sizing request originated from (telemetry + routing).
 */
export const SizerSurface = {
  PORTAL: 'PORTAL',
  WEBSITE: 'WEBSITE',
  SLACK: 'SLACK',
  API: 'API',
} as const;
export type SizerSurface = (typeof SizerSurface)[keyof typeof SizerSurface];

export const SizerSurfaceSchema = z.nativeEnum(SizerSurface);
